import { faArrowRight, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Blockquote, Button, StyledCheckbox } from '@ui';
import { cx } from 'class-variance-authority';
import { usePaymentTerms } from 'components/UserInvoice/data';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Suggestion, SuggestionValues } from './RecipientSuggestions';

interface Props {
    suggestion: Suggestion;
    /** Values currently held by the form, used to show what an apply would replace. */
    current: SuggestionValues;
    /** Every field the suggestions cover — the scope a clear may touch. */
    scope: string[];
    /** Whether fields without a suggested value are blanked instead of left alone. */
    clearEmpty?: boolean;
    onApply: (keys: string[], clearKeys: string[]) => void;
    onClose: () => void;
}

// Formats a form value for the preview, returning null for anything empty.
const display = (value: unknown) => {
    const text = String(value ?? '').trim();

    return text === '' ? null : text;
};

// Lets the user review each field a suggestion would change and pick which changes to keep.
export const SuggestionApplyModal = ({ suggestion, current, scope, clearEmpty, onApply, onClose }: Props) => {
    const { t } = useTranslation('invoice');

    const { data: paymentTermData } = usePaymentTerms();

    // Resolves what a stored value looks like to the user. Only payment_term_id differs from
    // its raw form: the form holds an id, the suggestion shows the readable term.
    const displayCurrent = (key: string) => {
        const raw = current[key];

        if (key !== 'payment_term_id' || raw === null || raw === undefined) {
            return display(raw);
        }

        const term = paymentTermData?.data?.find((entry) => String(entry.id) === String(raw));

        return term ? `${term.key} - ${term.title}` : display(raw);
    };

    const rows = useMemo(() => {
        const suggested = suggestion.fields.map((field) => ({
            key: field.key,
            next: field.value,
            previous: displayCurrent(field.key),
            // Compared on the raw values, not the rendered ones — an unchanged payment term
            // would otherwise always look like a change, since its label never equals its id.
            unchanged: String(suggestion.values[field.key] ?? '') === String(current[field.key] ?? ''),
            isClear: false
        }));

        // With the clearing option on, the fields that would be emptied are changes too —
        // listing them here is the only place a single one can be kept.
        const cleared = !clearEmpty
            ? []
            : scope
                  .filter((key) => !(key in suggestion.values) && displayCurrent(key) !== null)
                  .map((key) => ({
                      key,
                      next: null,
                      previous: displayCurrent(key),
                      unchanged: false,
                      isClear: true
                  }));

        return [...suggested, ...cleared].filter((row) => !row.unchanged);
    }, [suggestion, current, scope, clearEmpty, paymentTermData]);

    const [selected, setSelected] = useState<string[]>(() => rows.map((row) => row.key));
    const overwrites = rows.filter((row) => row.previous !== null);

    // Adds or removes a single field from the selection.
    const toggle = (key: string, checked: boolean) =>
        setSelected((keys) => (checked ? [...keys, key] : keys.filter((entry) => entry !== key)));

    // Hands the selection back, split into values to write and fields to blank.
    const submit = () => {
        const picked = rows.filter((row) => selected.includes(row.key));

        onApply(
            picked.filter((row) => !row.isClear).map((row) => row.key),
            picked.filter((row) => row.isClear).map((row) => row.key)
        );
        onClose();
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <Blockquote>{t('invoiceForm.recipientField.applyModal.intro', { name: suggestion.label })}</Blockquote>

            {rows.length === 0 ? (
                <Blockquote color="warning">{t('invoiceForm.recipientField.applyModal.noChanges')}</Blockquote>
            ) : (
                <>
                    {overwrites.length > 0 && (
                        <Blockquote color="warning" className="tw-flex tw-gap-3">
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                className="tw-mt-0.5 tw-shrink-0 tw-text-warning"
                            />
                            <span className="tw-text-sm">
                                {t('invoiceForm.recipientField.applyModal.overwriteHint', {
                                    count: overwrites.length
                                })}
                            </span>
                        </Blockquote>
                    )}

                    <div className="tw-flex tw-flex-col tw-gap-1">
                        <div className="tw-grid tw-grid-cols-[auto_1fr_1fr] tw-gap-3 tw-px-2 tw-pb-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-accents-8">
                            <span className="tw-w-5" />
                            <span>{t('invoiceForm.recipientField.applyModal.current')}</span>
                            <span>{t('invoiceForm.recipientField.applyModal.next')}</span>
                        </div>

                        {rows.map((row) => (
                            <label
                                key={row.key}
                                className={cx(
                                    'tw-grid tw-cursor-pointer tw-grid-cols-[auto_1fr_1fr] tw-items-start tw-gap-3 tw-rounded-xl tw-px-2 tw-py-2 tw-transition-colors',
                                    row.previous !== null ? 'tw-bg-warning/20' : 'hover:tw-bg-accents-1'
                                )}
                            >
                                <StyledCheckbox
                                    isSelected={selected.includes(row.key)}
                                    onChange={(checked) => toggle(row.key, checked)}
                                />
                                <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
                                    <span className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-accents-7">
                                        {t(`invoiceForm.recipientField.fields.${row.key}`)}
                                    </span>
                                    <span className="tw-break-words tw-text-sm tw-text-accents-8">
                                        {row.previous ?? '—'}
                                    </span>
                                </span>
                                <span className="tw-flex tw-min-w-0 tw-items-start tw-gap-2">
                                    <FontAwesomeIcon
                                        icon={faArrowRight}
                                        className="tw-mt-1.5 tw-shrink-0 tw-text-[10px] tw-text-accents-7"
                                    />
                                    <span
                                        className={cx(
                                            'tw-break-words tw-text-sm tw-font-medium',
                                            row.isClear ? 'tw-italic tw-text-accents-7' : 'tw-text-accents-9'
                                        )}
                                    >
                                        {row.isClear ? t('invoiceForm.recipientField.applyModal.cleared') : row.next}
                                    </span>
                                </span>
                            </label>
                        ))}
                    </div>
                </>
            )}

            <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-justify-end">
                <Button type="button" flat color="light" onPress={onClose}>
                    {t('invoiceForm.recipientField.applyModal.cancel')}
                </Button>
                <Button type="button" color="success" isDisabled={selected.length === 0} onPress={submit}>
                    {t('invoiceForm.recipientField.applyModal.submit', { count: selected.length })}
                </Button>
            </div>
        </div>
    );
};
